/**
 * Archivo: Boletines.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista de los boletines, mostrando el boletín actual y un carrusel de boletines pasados.
 * Incluye la funcionalidad para agregar, modificar y eliminar boletines, así como descargar archivos asociados.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/BoletinesServicios: Funciones para interactuar con el servicio de boletines.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera principal.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de navegación.
 * - Componentes/VentanaAñadirBoletin: Componente modal para añadir un nuevo boletín.
 * - Componentes/VentanaModificarBoletin: Componente modal para modificar un boletín existente.
 * 
 * Propósito:
 * Proveer una interfaz para gestionar los boletines, permitiendo su visualización, adición, modificación y eliminación,
 * así como la descarga de archivos PDF e imágenes asociados a cada boletín.
 */

import { default as React, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import VentanaAñadirBoletin from '../Componentes/VentanaAñadirBoletin';
import VentanaModificarBoletin from '../Componentes/VentanaModificarBoletin';
import useWindowSize from '../Componentes/TamañoVentana';
import '../Estilos/estilos.css';
import { listaBoletines, descargarBoletinPDF, descargarBoletinImagen } from '../Servicios/BoletinesServicios';

import pdf from '../Imagenes/Iconos/Pdf.png'
import imagen from '../Imagenes/Iconos/Imagen.png'
import lapizNegro from '../Imagenes/Iconos/LapizNegro.png'

//Función para renderizar los boletines que no son el boletín actual
const RenderOtrosBoletines = ({ urlBoletines, boletines, boletinSeleccionado, setBoletinSeleccionado, grupoActual, numBoletinesPorGrupo }) => {
  //console.log("En renderOtros: boletienes", boletines)
  const [segundoClick, setSegundoClick] = useState(false);
  const windowSize = useWindowSize();

  const handleClick = (index) => {
    const globalIndex = grupoActual * numBoletinesPorGrupo + index;

    if (boletinSeleccionado === globalIndex && segundoClick) {

      // Si se hace clic por segunda vez en el mismo boletín, hazlo más grande
      setSegundoClick(false);
      setBoletinSeleccionado(globalIndex);
    } else if (boletinSeleccionado === globalIndex && !segundoClick) {

      // Si se hace clic por primera vez en el mismo boletín, marca segundoClick como true
      setSegundoClick(true);
    } else {

      // Si se hace clic en un boletín diferente, selecciona ese boletín
      setSegundoClick(false);
      setBoletinSeleccionado(globalIndex);
    }
  };
  
  const getMarginLeft = (isGlobalIndex) => {
    if (windowSize.width <= 1000) {
      return isGlobalIndex ? '-30vw' : '-27vw';
    } else if (windowSize.width <= 1200) {
      return isGlobalIndex ? '-22vw' : '-21vw';
    } else if (windowSize.width <= 1550) {
      return isGlobalIndex ? '-16vw' : '-14vw';
    } else {
      return isGlobalIndex ? '-14vw' : '-12vw';
    }
  };

  const getWidth = (isGlobalIndex) => {
    if (windowSize.width <= 1000) {
      return isGlobalIndex ? '32vw' : '31vw';
    } else if (windowSize.width <= 1200) {
      return isGlobalIndex ? '29vw' : '26vw';
    } else if (windowSize.width <= 1550) {
      return isGlobalIndex ? '24vw' : '21vw';
    } else {
      return isGlobalIndex ? '23vw' : '20vw';
    }
  };

  return (
    <div className="container-fluid p-0 justify-content-center align-items-center d-flex pl-4">
      {boletines.map((boletin, index) => {
        const globalIndex = grupoActual * numBoletinesPorGrupo + index;
        const isGlobalIndex = boletinSeleccionado === globalIndex;

        return (
          <div
            key={index}
            style={{
              zIndex: isGlobalIndex ? boletines.length + 1 : boletines.length - index,
              marginLeft: index === 0 ? 0 : getMarginLeft(isGlobalIndex),
              cursor: 'pointer',
              position: 'relative',
              transform: segundoClick && isGlobalIndex ? 'scale(1.1)' : 'scale(1)',
              boxShadow: isGlobalIndex ? '0 0 20px rgba(0,0,0,0.5)' : 'none',
              transition: 'all 0.3s ease',
            }}
            onClick={() => handleClick(index)}
          >
            <img
              alt="boletin"
              src={urlBoletines + boletin.imagen_boletin}
              style={{
                width: getWidth(isGlobalIndex),
                height: '80%',
                border: '2px solid black',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};


function Boletines() {
  const urlBoletines = `${process.env.REACT_APP_URL_IMAGENES}BoletinesSemanales/`
  const [permisos, setPermisos] = useState(null);
  const [hayVentanas, setHayVentanas] = useState(false);
  const [boletinActual, setBoletinActual] = useState(null);
  const [token, setToken] = useState('');
  const [boletinSeleccionado, setBoletinSeleccionado] = useState(0);
  const [indiceGrupoBoletines, setIndiceGrupoBoletines] = useState(0);
  const numBoletinesPorGrupo = 6;
  const [mostrarAñadirBoletin, setMostrarAñadirBoletin] = useState(false);
  const [mostrarModificarBoletin, setMostrarModificarBoletin] = useState(false);
  const [modificarBoletinActual, setModificarBoletinActual] = useState(false)
  const [boletines, setBoletines] = useState(['']); 
  const [todosBoletines, setTodosBoletines] = useState(['']); 

  const handleAñadirBoletin = () => {
    setMostrarAñadirBoletin(true);
    setHayVentanas(true);
  };
  const closeAñadirBoletin = () => {
    setMostrarAñadirBoletin(false);
    setHayVentanas(false);
  };

  const handleModificarBoletin = (esBoletinActual = false) => {
    if (!esBoletinActual && !boletines[boletinSeleccionado]) {
      console.log('Boletín seleccionado no existe');
      return;
    }
    setModificarBoletinActual(esBoletinActual)
    setMostrarModificarBoletin(true);
    setHayVentanas(true);
  };

  const closeModificarBoletin = () => {
    setModificarBoletinActual(false)
    setMostrarModificarBoletin(false);
    setHayVentanas(false);
  };

  useEffect(() => {
    const obtenerDatos = async () => {
      const tokenNuevo = Cookies.get('token');
      const permisosNuevos = Cookies.get('permisos');
      setToken(tokenNuevo);
      setPermisos(permisosNuevos);
      //console.log("Token:", tokenNuevo, " Permisos:", permisosNuevos);
      await actualizarListaBoletines(tokenNuevo, permisosNuevos);
    };
    obtenerDatos();
  }, [token]);


  // Dividir los boletines en grupos
  const gruposBoletines = dividirEnGrupos(boletines, numBoletinesPorGrupo);

  const actualizarListaBoletines = async (token) => {
    //console.log("Token en actualizar lista:", token)
    const resultado = await listaBoletines(token);
    //console.log("Lista boletines:", resultado)
    if (resultado.success) {
      const boletinActual = resultado.data.find(boletin => boletin.esActual_boletin);
      const otrosBoletines = resultado.data.filter(boletin => !boletin.esActual_boletin);
      //console.log("Boletines", otrosBoletines)
      setBoletinActual(boletinActual);
      setBoletines(otrosBoletines);
      if (boletinActual) {
        setTodosBoletines([boletinActual, ...otrosBoletines]);
      } else {
        setTodosBoletines(otrosBoletines);
      }
      // console.log(boletinActual)
      // console.log(otrosBoletines)
      // console.log(todosBoletines)
    } else {
      console.log(resultado ? resultado.message : 'Error desconocido');
    }
  };

  // Función para dividir el array en grupos de tamaño especificado
  function dividirEnGrupos(array, tamañoGrupo) {
    const grupos = [];
    for (let i = 0; i < array.length; i += tamañoGrupo) {
      grupos.push(array.slice(i, i + tamañoGrupo));
    }
    return grupos;
  }

  const handleDescargarBoletinPDF = (id_boletin) => {
    const token = Cookies.get('token');
    descargarBoletinPDF(token, id_boletin)
        .then(response => {
            if (response.success) {
              const link = document.createElement('a');
              link.href = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
              link.download = `boletin_${id_boletin}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
              console.log(response.message);
            }
        })
        .catch(() => {
          console.log('Error al descargar PDF');
        });
  };

  const handleDescargarBoletinImagen = (id_boletin) => {
    const token = Cookies.get('token');
    descargarBoletinImagen(token, id_boletin)
    .then(response => {
        if (response.success) {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(new Blob([response.data], { type: 'image/jpeg' }));
            link.download = `imagen_${id_boletin}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
          console.log(response.message);
        }
    })
    .catch(() => {
        console.log('Error al descargar imagen');
    });
  };

  //Carrusel de boletines pasados, están divididos de 6 en 6
  const CarruselOtrosBoletines = ({ boletines, indiceGrupoBoletines }) => {
    //console.log("Boletin seleccionado:", boletinSeleccionado)
    //console.log("Boletines en carrusel:", boletines)
    //console.log("Boletines:", boletines)
    const handlePrev = () => {
      setIndiceGrupoBoletines(prevIndice => Math.max(0, prevIndice - 1));
    };
  
    const handleNext = () => {
      setIndiceGrupoBoletines(prevIndice => Math.min(gruposBoletines.length - 1, prevIndice + 1));
    };
  
    const buttonStyle = {
      position: 'absolute',
      top: '50%',
      zIndex: '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '5rem',
      height: '5rem',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      transform: 'translateY(-50%)',
      color: '#000',
    };
    const leftButtonStyle = {
      ...buttonStyle,
      left: '0rem',
      display: indiceGrupoBoletines === 0 ? 'none' : 'block',
    };
  
    const rightButtonStyle = {
      ...buttonStyle,
      right: '0rem',
      display: indiceGrupoBoletines === gruposBoletines.length - 1 ? 'none' : 'block',
    };
  
    return (
      <div id="carrusel" className="carousel-container" style={{ width: '100%', zIndex: '3', height: '100%', marginLeft: '-3rem' }}>
        <div className="row-fluid d-flex justify-content-between pb-2">
          <div className='d-flex col-xl-8'>
            <span className='text-black customFontSizeTextoNormal ml-4'>Boletines pasados:</span>
            <div>
              {boletines[boletinSeleccionado] && (
                <span className='text-black customFontSizeTextoNormal ml-4'>{boletines[boletinSeleccionado].nombre_boletin}</span>
              )}
            </div>
            {boletines[boletinSeleccionado].pdf_boletin !== '' &&
              <img alt="pdf" src={pdf} style={{ height: '2.5rem', width: '2.5rem', cursor:'pointer' }}
              onClick={() => handleDescargarBoletinPDF(boletines[boletinSeleccionado].id_boletin)}
              />
            }
            {boletines[boletinSeleccionado].imagen_boletin !== 'sinBoletin.png' &&
              <img alt="boletin" src={imagen} style={{ height: '2.5rem', width: '2.5rem', cursor:'pointer' }} 
              onClick={() => handleDescargarBoletinImagen(boletines[boletinSeleccionado].id_boletin)}
              />
            }
            {permisos === "Administrador" &&
              <div className="ml-1 mt-1">
                <img
                  alt="modificar"
                  src={lapizNegro}
                  style={{ height: '2rem', width: '2rem', cursor: 'pointer'}}
                  onClick={() => handleModificarBoletin(false)}
                />
              </div>
            }
          </div>
          {permisos === "Administrador" &&
            <div className='d-flex row-fluid'>
              <div className="col-1">
                <button className="btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleAñadirBoletin()}>
                  <span className='text-white'>Añadir boletín</span>
                </button>
              </div>
            </div>
          }
        </div>
        <div className='container-fluid align-content-center p-0 col-12'>
          <div className="carousel-inner justify-content-center align-content-center" style={{ height: '100%' }}>
            <div className='container-fluid p-0'>
              <div className="container-fluid d-flex justify-content-center align-content-center p-2" style={{ height: '90%' }}>
                <RenderOtrosBoletines 
                  urlBoletines={urlBoletines} 
                  boletines={gruposBoletines[indiceGrupoBoletines]}
                  boletinSeleccionado={boletinSeleccionado}
                  setBoletinSeleccionado={setBoletinSeleccionado}
                  grupoActual={indiceGrupoBoletines}
                  numBoletinesPorGrupo={numBoletinesPorGrupo}
                   />
              </div>
            </div>
          </div>
          <button style={leftButtonStyle} type="button" onClick={handlePrev}>
            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
          </button>
          <button style={rightButtonStyle} type="button" onClick={handleNext}>
            <span className="carousel-control-next-icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    );
  };

  const CarruselBoletinesMovil = ({ urlBoletines, boletines }) => {
    const [indiceBoletinActual, setIndiceBoletinActual] = useState(0);

    const handlePrev = () => {
        if (indiceBoletinActual > 0) {
            setIndiceBoletinActual(prev => prev - 1);
        }
    };

    const handleNext = () => {
        if (indiceBoletinActual < boletines.length - 1) {
            setIndiceBoletinActual(prev => prev + 1);
        }
    };
    const buttonStyle = {
        position: 'absolute',
        top: '50%',
        zIndex: '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3rem',
        height: '3rem',
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        transform: 'translateY(-50%)',
        color: '#fff',
    };
    const leftButtonStyle = {
        ...buttonStyle,
        left: '0.5rem',
        display: indiceBoletinActual === 0 ? 'none' : 'block',
    };
    const rightButtonStyle = {
        ...buttonStyle,
        right: '0.5rem',
        display: indiceBoletinActual === boletines.length - 1 ? 'none' : 'block',
    };

    if (!boletines || boletines.length === 0) {
        return <div>No hay boletines disponibles.</div>;
    }

    const boletinActual = boletines[indiceBoletinActual];

    return (
        <div id="carrusel" className="carousel-container position-relative" style={{ width: '100%', zIndex: '3', height: '90vh' }}>
            <div className="d-flex flex-column align-items-start" style={{ height: '100%', overflow: 'hidden' }}>
                <div className="row-fluid d-flex p-2">
                    <div className='ml-3 d-flex'>
                        <span className='customFontSizeTextoGrande'> Boletín {boletinActual.esActual_boletin ? 'actual:' : 'pasado:'} </span>
                    </div>
                    <div className='ml-2'>
                        <span className="text-black customFontSizeTextoGrande" style={{ fontWeight: 'bold' }}>{boletinActual.nombre_boletin}</span>
                    </div>

                    <div className="d-flex justify-content-center">
                        {boletinActual.pdf_boletin && (
                            <img alt="pdf" src={pdf} style={{ height: '2rem', width: '2rem', cursor: 'pointer', margin: '0 0.5rem' }}
                                onClick={() => handleDescargarBoletinPDF(boletinActual.id_boletin)} />
                        )}
                        {boletinActual.imagen_boletin && boletinActual.imagen_boletin !== 'sinBoletin.png' && (
                            <img alt="boletin" src={imagen} style={{ height: '2rem', width: '2rem', cursor: 'pointer', margin: '0 0.5rem' }}
                                onClick={() => handleDescargarBoletinImagen(boletinActual.id_boletin)} />
                        )}
                    </div>
                </div>
                <div className="d-flex justify-content-center align-items-center" style={{ width: '100%', height:'100%' }}>
                    <img
                        alt="boletin"
                        src={urlBoletines + boletinActual.imagen_boletin}
                        style={{ width: '90%', border: '2px solid black' }}
                    />
                </div>
            </div>
            <button style={leftButtonStyle} type="button" onClick={handlePrev}>
                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
            </button>
            <button style={rightButtonStyle} type="button" onClick={handleNext}>
                <span className="carousel-control-next-icon" aria-hidden="true"></span>
            </button>
        </div>
    );
};
    
  const renderBoletinActual = (boletin) => {
    if (!boletin) {
      return <div>No hay ningún boletín actual.</div>;
    }
  
    return (
      <div className="container-fluid p-0" style={{ flex: '0 0 auto', cursor: 'pointer' }}>
        <div className="row-fluid d-flex justify-content-start pb-2" style={{ backgroundColor: 'white' }}>
          <div className='flex-container'>
            <span className='text-black customFontSizeTextoNormal mt-2'>Boletin actual:</span>
            <div className='mt-2'>
              <span className='text-black customFontSizeTextoNormal ml-2'>{boletin.nombre_boletin}</span>
            </div>
          </div>
          {boletin.pdf_boletin !== '' && 
            <img alt="pdf" src={pdf} style={{ height: '3rem', width: '3rem' }} 
            onClick={() => handleDescargarBoletinPDF(boletin.id_boletin)}/>
          }
          {boletin.imagen_boletin !== 'sinBoletin.png' &&
            <img alt="boletin" src={imagen} style={{ height: '3rem', width: '3rem' }} 
            onClick={() => handleDescargarBoletinImagen(boletin.id_boletin)}/>
          }
          { permisos === "Administrador" &&
            <div className="ml-1 mt-2">
              <img
                alt="modificar"
                src={lapizNegro}
                style={{ height: '2rem', width: '2rem', cursor: 'pointer'}}
                onClick={() => handleModificarBoletin(true)}
              />
            </div>
          }
        </div>
        <div className='container-fluid p-0 d-flex align-items-center' style={{height:'90%'}}>
          <div className="container-fluid p-0 col-10 mt-3" style={{ border: '2px solid black' }}>
            <img alt="boletin" src={urlBoletines + boletin.imagen_boletin} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>
      </div>
    )
  };
  
    return (
    <>
    <MetaTags />
    <div className="fondo">
        {/**CABECERA*/}
        <Cabecera permisos={permisos}/>
        {/** PANTALLA GRANDE */}
        <div className='d-none d-md-block '>
            {/**SEGUNDA CABECERA */}
            <SegundaCabecera hayVentanas={hayVentanas} permisos={permisos}  />
            {/**BOLETINES*/}
            {mostrarAñadirBoletin && <VentanaAñadirBoletin 
              onClose={closeAñadirBoletin} 
              actualizarLista={async () => {
                await actualizarListaBoletines(Cookies.get('token'));
              }}
            />}
            {mostrarModificarBoletin && (<VentanaModificarBoletin 
              onClose={closeModificarBoletin} 
              actualizarLista={async () => {
                await actualizarListaBoletines(Cookies.get('token'));
              }}
              boletin={modificarBoletinActual ? boletinActual : boletines[boletinSeleccionado]}
              setBoletinSeleccionado={setBoletinSeleccionado}
            />
          )}
            <div className='container-fluid d-flex p-2'>
              <div className='container-fluid col-xl-4 col-md-5 col-sm-5 justify-content-start' style={{ height:'calc(100vh - 15rem)',  display: 'grid', opacity: hayVentanas ? 0.4 : 1 }}>
                {renderBoletinActual(boletinActual)}
              </div>
              <div className='container-fluid col-xl-8 col-md-7 col-sm-7 p-2' style={{ height:'calc(100vh - 9rem)', opacity: hayVentanas ? 0.4 : 1 }}>
                <CarruselOtrosBoletines 
                  boletines={boletines} 
                  indiceGrupoBoletines={indiceGrupoBoletines} 
                />
              </div>
            </div>
        </div>
        {/**PARA PANTALLAS PEQUEÑAS*/}
        <div className='d-block d-md-none'>
          <div className='container-fluid p-2' style={{ height: 'calc(100vh - 9rem)', opacity: hayVentanas ? 0.4 : 1 }}>
            <CarruselBoletinesMovil 
              urlBoletines={urlBoletines} 
              boletines={todosBoletines} 
            />
          </div>
        </div>
        {/** Pantalla de overlay para que no se pueda seleccionar ningún objeto o interactuar si hay alguna ventana */}
        {hayVentanas && (
          <div className="overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2
          }}></div>
        )}
    </div>
    </>
    );
};

export default Boletines;